// Shared color tokens, SVG chart builders, and per-pair mock-chart helpers for the Customers & Products
// detail pages. Both pages render the same SVG charts and (for entities with no real order events) the same
// deterministic mock calendar/distribution variations — so this is the single source of truth for that logic.
// The SVG string builders are ported verbatim from the HTML mock and inject hardcoded hex; the page-level
// per-entity color helpers that differ between the two pages (riskColor/riskBg/etc.) stay in each page.
import type React from 'react';

import { C } from './theme';

/* ── Shared info box (blue-tinted intro panel above the behavior charts) ── */
export const introBox = (text: React.ReactNode, title: string) => (
  <div
    style={{
      padding: '10px 14px',
      background: 'rgba(39,68,131,.05)',
      borderRadius: 6,
      borderLeft: `3px solid ${C.blue}`,
    }}
  >
    <div style={{ fontSize: 10.5, color: C.blue, fontWeight: 700, marginBottom: 2 }}>{title}</div>
    <div style={{ fontSize: 10.5, color: C.t2, lineHeight: 1.5 }}>{text}</div>
  </div>
);

/* ── Distribution chart datum ─────────────────────────────────────────── */
export type Distribution = { label: string; bars: number[]; labels: string[]; p50: string; p75?: string; p90?: string };

/* ── Raw HTML/SVG injector (chart builders return verbatim mock strings) ── */
export const Raw = ({ html, style }: { html: string; style?: React.CSSProperties }) => (
  <div style={style} dangerouslySetInnerHTML={{ __html: html }} />
);

/* ── SVG chart builders (ported verbatim from mock) ───────────────────── */
// SVG heatmap (was HTML divs) so it scales to fill the card width — keeping the day-of-week (M–S) axis visible —
// and zooms in the modal via enlargeSvgs (which rewrites the SVG height).
export function calHtml(data: number[][]) {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const cell = 10,
    gap = 1,
    labelW = 13;
  const maxVal = Math.max(...data.flat()) || 1;
  const w = labelW + data.length * (cell + gap);
  const h = days.length * (cell + gap);
  let svg = `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto">`;
  days.forEach((d, r) => {
    svg += `<text x="${labelW - 3}" y="${r * (cell + gap) + cell - 1.5}" font-size="7" fill="#9295A5" text-anchor="end" font-family="Inter,sans-serif">${d}</text>`;
  });
  data.forEach((week, c) => {
    week.forEach((val, r) => {
      const x = labelW + c * (cell + gap);
      const y = r * (cell + gap);
      const intensity = val / maxVal;
      const bg = val === 0 ? 'rgba(39,68,131,.05)' : `rgba(39,68,131,${(0.1 + intensity * 0.85).toFixed(2)})`;
      svg += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" rx="2" fill="${bg}"/>`;
    });
  });
  return svg + '</svg>';
}

export function intervalHtml(v: number[]) {
  const w = 380,
    h = 90,
    max = 42,
    pad = 20,
    bw = (w - pad - 6) / v.length - 1;
  return (
    `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:90px">` +
    [0, 14, 28, 42]
      .map(
        (val) =>
          `<text x="${pad - 2}" y="${h - 14 - (val / max) * (h - 22) + 3}" font-size="7" fill="#9295A5" text-anchor="end" font-family="Inter,sans-serif">${val}</text>`,
      )
      .join('') +
    `<line x1="${pad}" y1="${h - 14}" x2="${w - 6}" y2="${h - 14}" stroke="#E4E0D8" stroke-width="0.5"/>` +
    v
      .map((val, i) => {
        const x = (pad + i * (bw + 1)).toFixed(1),
          bh = ((val / max) * (h - 22)).toFixed(1),
          y = (h - 14 - parseFloat(bh)).toFixed(1);
        return `<rect x="${x}" y="${y}" width="${bw.toFixed(1)}" height="${bh}" fill="#274483" rx="1" opacity="0.8"/>`;
      })
      .join('') +
    `<text x="${w / 2}" y="${h - 2}" font-size="7" fill="#9295A5" text-anchor="middle" font-family="Inter,sans-serif">Order # (Last 45)</text></svg>`
  );
}

export function qtyHtml(v: number[]) {
  const w = 380,
    h = 100,
    max = Math.max(...v) * 1.1,
    pad = 30,
    xs = (w - pad - 6) / v.length;
  const avg = Math.round(v.reduce((a, b) => a + b, 0) / v.length);
  const avgY = (h - 18 - (avg / max) * (h - 26)).toFixed(1);
  const pts = v
    .map((val, i) => (pad + i * xs).toFixed(1) + ',' + (h - 18 - (val / max) * (h - 26)).toFixed(1))
    .join(' ');
  return (
    `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100px">` +
    [0, Math.round(max / 2), Math.round(max)]
      .map((val) => {
        const y = (h - 18 - (val / max) * (h - 26)).toFixed(1);
        return (
          `<text x="${pad - 2}" y="${parseFloat(y) + 3}" font-size="7" fill="#9295A5" text-anchor="end" font-family="Inter,sans-serif">${val ? (max >= 1000 ? Math.round(val / 1000) + 'K' : Math.round(val).toLocaleString()) : 0}</text>` +
          `<line x1="${pad}" y1="${y}" x2="${w - 6}" y2="${y}" stroke="#E4E0D8" stroke-width="0.5"/>`
        );
      })
      .join('') +
    `<polygon points="${pts} ${w - 6},${h - 18} ${pad},${h - 18}" fill="rgba(39,68,131,0.07)"/>` +
    `<polyline points="${pts}" fill="none" stroke="#274483" stroke-width="1.8" stroke-linejoin="round"/>` +
    `<line x1="${pad}" y1="${avgY}" x2="${w - 6}" y2="${avgY}" stroke="#D27A24" stroke-width="1.5" stroke-dasharray="5,3"/>` +
    v
      .map(
        (val, i) =>
          `<circle cx="${(pad + i * xs).toFixed(1)}" cy="${(h - 18 - (val / max) * (h - 26)).toFixed(1)}" r="2" fill="#274483"/>`,
      )
      .join('') +
    `<text x="${w - 8}" y="${parseFloat(avgY) - 3}" font-size="7" fill="#D27A24" text-anchor="end" font-family="Inter,sans-serif">Avg</text>` +
    `<text x="${w / 2}" y="${h - 3}" font-size="7" fill="#9295A5" text-anchor="middle" font-family="Inter,sans-serif">Order # (Last 45)</text></svg>`
  );
}

export function distHtml(dists: Distribution[]) {
  return (dists || [])
    .map((d) => {
      const max = Math.max(...d.bars) || 1;
      const bw = Math.floor(200 / d.bars.length) - 2;
      let svg = `<svg viewBox="0 0 200 64" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:64px">`;
      d.bars.forEach((val, i) => {
        const bh = Math.round((val / max) * 50);
        const x = i * (bw + 2);
        svg += `<rect x="${x}" y="${54 - bh}" width="${bw}" height="${bh}" fill="${val === max ? '#274483' : 'rgba(39,68,131,0.3)'}" rx="1"/>`;
        if (d.labels[i])
          svg += `<text x="${x + bw / 2}" y="62" font-size="6.5" fill="#9295A5" text-anchor="middle" font-family="Inter,sans-serif">${d.labels[i]}</text>`;
      });
      svg += '</svg>';
      return `<div style="margin-bottom:10px">
        <div style="font-size:10px;font-weight:700;color:#5C5F72;margin-bottom:4px">${d.label}</div>
        ${svg}
        <div style="display:flex;gap:12px;margin-top:2px">
          <span style="font-size:9px;color:#9295A5">P50: ${d.p50}</span>
          ${d.p75 ? `<span style="font-size:9px;color:#9295A5">P75: ${d.p75}</span>` : ''}
          ${d.p90 ? `<span style="font-size:9px;color:#9295A5">P90: ${d.p90}</span>` : ''}
        </div>
      </div>`;
    })
    .join('');
}

/* ── Per-pair mock charts (calendar variation + real-array histograms) ──── */
// The event-behavior API gives real interarrival/quantity arrays per (customer×product), so the two
// distribution charts are histograms of that REAL data and change with the selection (see histDist).
// There is no per-pair monthly calendar in the feed, so the calendar uses one of 4 deterministic mock
// variations chosen by a hash of the selection — the same customer/pair always maps to the same variation.
export function hashKey(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
export function seededCalendar(seed: number): number[][] {
  let s = (seed ^ 0x9e3779b9) >>> 0;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  const weeks = 26;
  const grid: number[][] = [];
  for (let w = 0; w < weeks; w++) {
    const row: number[] = [];
    for (let d = 0; d < 7; d++) {
      const weekday = d < 5;
      const active = weekday ? rnd() > 0.45 : rnd() > 0.82;
      row.push(active ? 1 + Math.floor(rnd() * 5) : 0);
    }
    grid.push(row);
  }
  return grid;
}
export const CAL_VARIATIONS: number[][][] = [11, 29, 47, 83].map(seededCalendar);
// Histogram (bars + labels + P50/P75/P90) from a real value series.
export function histDist(label: string, values: number[], unit: string): Distribution {
  const vals = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (vals.length < 2) return { label, bars: [], labels: [], p50: '—' };
  const pct = (p: number) => vals[Math.min(vals.length - 1, Math.floor((p / 100) * vals.length))];
  const min = vals[0];
  const max = vals[vals.length - 1];
  const nb = 6;
  const span = max - min || 1;
  const bars = new Array(nb).fill(0);
  for (const v of vals) {
    const idx = Math.min(nb - 1, Math.max(0, Math.floor(((v - min) / span) * nb)));
    bars[idx]++;
  }
  const labels = bars.map((_, i) => String(Math.round(min + (span * (i + 0.5)) / nb)));
  return {
    label,
    bars,
    labels,
    p50: `${Math.round(pct(50))}${unit}`,
    p75: `${Math.round(pct(75))}${unit}`,
    p90: `${Math.round(pct(90))}${unit}`,
  };
}
